/**
 * AutoPlayAnimation - Auto-playing animation wrapper for landing page.
 * Uses the production animation system with auto-play and loop enabled.
 */

import { useEffect, useRef } from 'react'
import { AnimationProvider, useAnimation } from '@/contexts/AnimationContext'
import { AnimationCanvas } from '@/components/animation/AnimationCanvas'
import { useAnimationTiming } from '@/hooks/UseAnimationTiming'
import type { PlayContent } from '@/api/queries/PlayQueries'
import './auto-play-animation.css'

type AutoPlayAnimationProps = {
	play: PlayContent
	className?: string
}

/**
 * Inner component that has access to AnimationContext.
 */
function AutoPlayAnimationInner({ play, className }: AutoPlayAnimationProps) {
	const { state, loadPlay, play: startPlay, dispatch } = useAnimation()
	const hasLoadedRef = useRef(false)
	const playIdRef = useRef<string | null>(null)

	// Calculate animation timing from play data
	const animationPayload = useAnimationTiming({
		id: play.id,
		players: play.players,
		drawings: play.drawings,
	})

	// Load play and start animation
	useEffect(() => {
		if (!animationPayload) return

		// Only load if this is a new play or hasn't been loaded yet
		if (hasLoadedRef.current && playIdRef.current === play.id) {
			return
		}

		// Load the play data
		loadPlay(animationPayload)
		playIdRef.current = play.id
		hasLoadedRef.current = true
	}, [animationPayload, loadPlay, play.id])

	// Enable loop mode and start playing once loaded
	useEffect(() => {
		if (state.playId !== play.id) return
		if (state.phase !== 'ready') return

		// Enable loop mode
		if (!state.loopMode) {
			dispatch({ type: 'TOGGLE_LOOP' })
		}

		// Start playing
		startPlay()
	}, [state.playId, state.phase, state.loopMode, play.id, dispatch, startPlay])

	// Resume animation when tab becomes visible again
	useEffect(() => {
		function handleVisibilityChange() {
			if (!document.hidden && state.playId === play.id && !state.isPlaying) {
				// Tab became visible and we're not playing - resume
				startPlay()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [state.playId, state.isPlaying, play.id, startPlay])

	return (
		<div className={`auto-play-animation ${className ?? ''}`.trim()}>
			<AnimationCanvas
				drawings={play.drawings}
				players={play.players}
				className="auto-play-animation-canvas"
			/>
		</div>
	)
}

/**
 * Auto-playing animation component for landing page demos.
 * Wraps content in AnimationProvider and handles auto-play/loop setup.
 */
export function AutoPlayAnimation(props: AutoPlayAnimationProps) {
	return (
		<AnimationProvider>
			<AutoPlayAnimationInner {...props} />
		</AnimationProvider>
	)
}
